<template>
  <form @submit.prevent="storeQuotation" class="overflow-y-scroll">
    <page-layout>
      <template #header>
        <page-header :title="t('quotation_create')"></page-header>
      </template>

      <template #content>
        <div
          class="space-y-8 max-w-7xl mx-auto py-10 sm:px-6 lg:px-8 min-h-screen"
        >
          <ui-card>
            <template #header>
              {{ t("title") }}
            </template>

            <div
              class="
                flex flex-wrap
                items-end
                justify-between
                col-span-full
                space-y-3
                sm:space-y-0
              "
            >
              <div class="space-y-3 w-full sm:w-auto">
                <ui-input
                  disabled
                  type="text"
                  :label="t('author')"
                  :value="$page.props.user.data.name"
                ></ui-input>
              </div>

              <div class="space-y-3 w-full sm:w-auto">
                <ui-select
                  :options="customerOptions"
                  :placeholder="t('select_customer')"
                  :invalid="'customer' in form.errors"
                  :error="form.errors.customer"
                  :cornerHint="t('optional')"
                  :label="t('customer')"
                  v-model.number="form.customer"
                ></ui-select>
                <ui-select
                  :options="quotationStatuses"
                  :placeholder="t('select_status')"
                  :invalid="'status' in form.errors"
                  :error="form.errors.status"
                  :label="t('status')"
                  v-model="form.status"
                ></ui-select>
              </div>
            </div>

            <ui-divider class="my-6">
              <div class="relative flex items-center justify-between">
                <span class="pr-3 bg-white text-lg font-medium text-gray-900">
                  {{ t("concepts") }}
                </span>
                <button
                  type="button"
                  @click="addConcept"
                  class="
                    inline-flex
                    items-center
                    shadow-sm
                    px-4
                    py-1.5
                    border border-green-300
                    text-sm
                    leading-5
                    font-medium
                    rounded-full
                    text-green-700
                    bg-white
                    hover:bg-green-50
                    focus:outline-none
                    focus:ring-2
                    focus:ring-offset-2
                    focus:ring-green-500
                  "
                >
                  <PlusIcon
                    class="-ml-1.5 mr-1 h-5 w-5 text-green-400"
                    aria-hidden="true"
                  />
                  <span>{{ t("add_concept") }}</span>
                </button>
              </div>
            </ui-divider>

            <div class="w-full space-y-6">
              <div
                v-for="(concept, index) in form.concepts"
                :key="index"
                class="w-full space-y-4"
              >
                <div class="justify-end flex md:col-span-6">
                  <ui-button
                    backgroundStyling="bg-white hover:bg-red-50"
                    textColorStyling="text-red-400"
                    borderStyling="border border-red-400"
                    focusColorStyling="focus:ring-red-500"
                    paddingStyling="px-2.5 py-1.5"
                    textSizeStyling="text-xs"
                    type="button"
                    @click="form.concepts.splice(index, 1)"
                  >
                    {{ t("delete") }}
                  </ui-button>
                </div>

                <div class="grid grid-cols-3 gap-3">
                  <div class="col-span-2">
                    <ui-select
                      :options="physicalServiceOptions"
                      :placeholder="t('select_concept')"
                      :invalid="'physicalService' in form.errors"
                      :error="form.errors.physicalService"
                      :label="t('concept')"
                      v-model.number="form.concepts[index].service"
                      @change="copyServiceInfoToConceptIndex(index)"
                      v-if="form.concepts[index].service_type == 'physical'"
                    ></ui-select>
                    <ui-select
                      :options="chemicalServiceOptions"
                      :placeholder="t('select_concept')"
                      :invalid="'physicalService' in form.errors"
                      :error="form.errors.physicalService"
                      :label="t('concept')"
                      v-model.number="form.concepts[index].service"
                      @change="copyServiceInfoToConceptIndex(index)"
                      v-if="form.concepts[index].service_type == 'chemical'"
                    ></ui-select>
                    <ui-input
                      :invalid="'concept' in form.errors"
                      :error="form.errors.concept"
                      type="text"
                      :label="t('concept')"
                      v-model="form.concepts[index].concept"
                      v-if="form.concepts[index].service_type == null"
                    ></ui-input>
                  </div>

                  <ui-select
                    :options="serviceTypeOptions"
                    :placeholder="t('select_service_type')"
                    :label="t('service_type')"
                    v-model.number="form.concepts[index].service_type"
                    @change="clearInputs(index)"
                  ></ui-select>

                  <ui-input
                    :invalid="'quantity' in form.errors"
                    :error="form.errors.quantity"
                    type="number"
                    min="1"
                    :label="t('quantity')"
                    v-model.number="form.concepts[index].quantity"
                  ></ui-input>

                  <ui-input
                    :invalid="'unitary_price' in form.errors"
                    :error="form.errors.unitary_price"
                    type="number"
                    min="0"
                    step="any"
                    :label="t('unitary_price')"
                    v-model.number="form.concepts[index].unitary_price"
                  ></ui-input>

                  <ui-input
                    :invalid="'tax' in form.errors"
                    :error="form.errors.tax"
                    type="number"
                    min="0"
                    max="100"
                    step="any"
                    :label="t('tax %')"
                    v-model.number="form.concepts[index].tax"
                  ></ui-input>

                  <ui-input
                    :invalid="'area' in form.errors"
                    :error="form.errors.area"
                    type="text"
                    :label="t('area')"
                    v-model="form.concepts[index].area"
                    v-if="form.concepts[index].service_type != null"
                  ></ui-input>

                  <ui-select
                    :placeholder="t('type_placeholder')"
                    :options="typeOptions"
                    :label="t('type')"
                    v-model.number="form.concepts[index].type"
                    v-if="form.concepts[index].service_type != null"
                  >
                  </ui-select>

                  <div class="col-span-3">
                    <ui-textarea
                      :invalid="'description' in form.errors"
                      :error="form.errors.description"
                      type="text"
                      :label="t('description')"
                      v-model="form.concepts[index].description"
                      :cornerHint="t('optional')"
                    ></ui-textarea>
                  </div>
                </div>

                <ui-divider
                  v-if="index < form.concepts.length - 1"
                  class="my-6 pb-3 md:col-span-6"
                ></ui-divider>
              </div>

              <div
                class="col-span-2 flex flex-col items-end"
                v-show="form.concepts.length"
              >
                <p class="text-gray-600 text-sm">
                  {{
                    t("subtotal", {
                      subtotal: summary.subtotal
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                    })
                  }}
                </p>
                <p class="text-gray-600 text-sm">
                  {{
                    t("tax", {
                      tax: summary.tax
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                    })
                  }}
                </p>
                <p class="text-gray-700 font-bold text-xl">
                  {{
                    t("total", {
                      total: summary.total
                        .toString()
                        .replace(/\B(?=(\d{3})+(?!\d))/g, ","),
                    })
                  }}
                </p>
              </div>
            </div>

            <template #footer>
              <div class="w-full flex justify-end space-x-2">
                <Link :href="route('quotations.index')">
                  <ui-button
                    backgroundStyling="bg-white"
                    borderStyling="border border-gray-300"
                    textColorStyling="text-gray-700 hover:text-gray-500"
                    type="button"
                    >{{ t("cancel") }}</ui-button
                  >
                </Link>

                <ui-button
                  :disabled="form.processing"
                  type="submit"
                  class="ml-4"
                >
                  {{ t("save") }}
                </ui-button>
              </div>
            </template>
          </ui-card>
        </div>
      </template>
    </page-layout>
  </form>
</template>

<script>
import PageHeader from "./../Shared/Organisms/PageHeader";
import AppLayout from "./../Layouts/AppLayout";
import PageLayout from "./../Layouts/PageLayout";
import { useI18n } from "vue-i18n";
import { PlusIcon } from "@heroicons/vue/solid";

export default {
  layout: AppLayout,

  props: [
    "physicalServices",
    "chemicalServices",
    "customers",
    "quotationStatuses",
  ],

  data() {
    return {
      form: this.$inertia.form(
        {
          _method: "POST",
          customer: "",
          status: "",
          total: null,
          concepts: [],
        },
        {
          bag: "storeQuotation",
          resetOnSuccess: false,
        }
      ),
    };
  },

  mounted() {
    this.addConcept();
  },

  setup() {
    const { t } = useI18n();
    return { t };
  },

  methods: {
    storeQuotation() {
      this.form.post(this.route("quotations.store"), {
        preserveScroll: true,
      });
    },

    copyServiceInfoToConceptIndex(index) {
      if (this.form.concepts[index].service_type == "physical") {
        this.form.concepts[index].unitary_price =
          this.physicalServices.data.find((obj) => {
            return obj.id === this.form.concepts[index].service;
          }).price / 100;

        this.form.concepts[index].concept = this.physicalServices.data.find(
          (obj) => {
            return obj.id === this.form.concepts[index].service;
          }
        ).name;

        this.form.concepts[index].description = this.physicalServices.data.find(
          (obj) => {
            return obj.id === this.form.concepts[index].service;
          }
        ).description;
      }
      if (this.form.concepts[index].service_type == "chemical") {
        this.form.concepts[index].unitary_price =
          this.chemicalServices.data.find((obj) => {
            return obj.id === this.form.concepts[index].service;
          }).price / 100;

        this.form.concepts[index].concept = this.chemicalServices.data.find(
          (obj) => {
            return obj.id === this.form.concepts[index].service;
          }
        ).name;

        this.form.concepts[index].description = this.chemicalServices.data.find(
          (obj) => {
            return obj.id === this.form.concepts[index].service;
          }
        ).description;
      }
    },

    clearInputs(index) {
      this.form.concepts[index].unitary_price = 0;
      this.form.concepts[index].concept = null;
      this.form.concepts[index].service = null;
      this.form.concepts[index].quantity = 1;
      this.form.concepts[index].tax = 0;
      this.form.concepts[index].description = "";
      this.form.concepts[index].type = "";
      this.form.concepts[index].area = "";
    },

    addConcept() {
      this.form.concepts.push({
        concept: null,
        service: null,
        service_type: null,
        description: "",
        quantity: 1,
        unitary_price: 0,
        tax: 0,
        type: "",
        area: "",
      });
    },
  },

  computed: {
    typeOptions() {
      return [
        { text: "Personal", value: "personal" },
        { text: "Area", value: "area" },
      ];
    },
    serviceTypeOptions() {
      return [
        { text: "", value: null },
        { text: "Físico", value: "physical" },
        { text: "Químico", value: "chemical" },
      ];
    },
    customerOptions() {
      if (this.customers.data.length > 0) {
        return this.customers.data.map((customer) => {
          return {
            text: customer.name,
            value: customer.id,
          };
        });
      }
      return [];
    },
    physicalServiceOptions() {
      if (this.physicalServices.data.length > 0) {
        return this.physicalServices.data.map((physicalService) => {
          return {
            text:
              physicalService.name +
              " (" +
              physicalService.formatted_price +
              ")",
            value: physicalService.id,
            price: physicalService.price,
          };
        });
      }
      return [];
    },
    chemicalServiceOptions() {
      if (this.chemicalServices.data.length > 0) {
        return this.chemicalServices.data.map((chemicalService) => {
          return {
            text:
              chemicalService.name +
              " (" +
              chemicalService.formatted_price +
              ")",
            value: chemicalService.id,
            price: chemicalService.price,
          };
        });
      }
      return [];
    },
    summary() {
      let total = 0;
      let subtotal = 0;
      let tax = 0;
      this.form.concepts.forEach((element) => {
        total +=
          element.unitary_price * element.quantity * (1 + element.tax / 100);
        subtotal += element.unitary_price * element.quantity;
        tax += element.unitary_price * element.quantity * (element.tax / 100);
      });

      this.form.total = total.toFixed(2);

      return {
        total: total.toFixed(2),
        subtotal: subtotal.toFixed(2),
        tax: tax.toFixed(2),
      };
    },
  },

  components: {
    PageLayout,
    PageHeader,
    AppLayout,
    PlusIcon,
  },
};
</script>

<i18n>
{
  "es": {
    "quotation_create": "Crear nueva cotización",
    "title": "Modifica los detalles de la cotización",
    "author": "Autor",
    "customer": "Cliente",
    "select_customer": "Selecciona un cliente",
    "select_status": "Selecciona un estatus",
    "status": "Estatus",
    "total": "Total ${total} MXN",
    "subtotal": "Subtotal ${subtotal} MXN",
    "tax": "Impuestos ${tax} MXN",
    "description": "Descripción",
    "quantity": "Cantidad",
    "concept": "Concepto",
    "concepts": "Conceptos",
    "unitary_price": "Precio unitario",
    "tax %": "Impuestos %",
    "select_concept": "Selecciona un concepto",
    "add_concept": "Agregar",
    "optional": "opcional",
    "save": "Guardar",
    "cancel": "Cancelar",
    "delete": "Eliminar",
    "reorder": "Reordenar",
    "area": "Area",
    "type": "Tipo de medición",
    "type_placeholder": "Selecciona un tipo",
    "service_type": "Tipo de servicio",
    "select_service_type": "Selecciona un tipo de servicio"
  }
}
</i18n>
